<template>
  <div class="container-fluid py-3">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <h4 class="mb-0">
        <feather-icon
          icon="TruckIcon"
          size="20"
          class="mr-50"
        />
        {{ $t('despacho.title') }}
      </h4>
      <div class="d-flex" style="gap: 8px">
        <button
          v-if="$hasAbility('despacho.painel_equipes')"
          class="btn btn-sm btn-outline-primary"
          @click="$router.push({ name: 'despacho-team-panel' })"
        >
          <feather-icon
            icon="GridIcon"
            size="14"
            class="mr-25"
          />
          {{ $t('despacho.teamPanelButton') }}
        </button>
        <button
          v-if="$hasAbility('despacho.atualizar')"
          class="btn btn-sm btn-outline-secondary"
          @click="loadOccurrences"
        >
          <feather-icon
            icon="RefreshCwIcon"
            size="14"
            class="mr-25"
          />
          {{ $t('despacho.refresh') }}
        </button>
      </div>
    </div>

    <!-- KPI Cards -->
    <div class="row mb-3">
      <div
        v-for="card in kpiCards"
        :key="card.key"
        class="col-6 col-md-4 col-xl-2 mb-2"
      >
        <div
          class="kpi-card"
          :class="[
            card.variant,
            {
              'kpi-card--clickable': card.filterable,
              'kpi-card--active': card.filterable && filtroKpi === card.key,
            },
          ]"
          :role="card.filterable ? 'button' : null"
          :tabindex="card.filterable ? 0 : null"
          :aria-pressed="card.filterable ? String(filtroKpi === card.key) : null"
          @click="toggleKpi(card)"
          @keydown.enter.prevent="toggleKpi(card)"
          @keydown.space.prevent="toggleKpi(card)"
        >
          <div class="kpi-card__icon">
            <feather-icon
              :icon="card.icon"
              size="20"
            />
          </div>
          <div class="kpi-card__body">
            <div class="kpi-card__value">
              <b-spinner
                v-if="loadingKpis"
                small
              />
              <span v-else>{{ card.value }}</span>
            </div>
            <div class="kpi-card__label">
              {{ card.label }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="filtroKpi"
      class="kpi-filter mb-3"
    >
      <feather-icon
        icon="FilterIcon"
        size="14"
      />
      <span>
        {{ $t('despacho.filter.filteringBy') }} <strong>{{ rotuloFiltroKpi }}</strong> &mdash;
        {{ occurrencesFiltradas.length }} {{ $t('despacho.of') }} {{ occurrences.length }}
      </span>
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary ml-auto"
        @click="filtroKpi = null"
      >
        <feather-icon
          icon="XIcon"
          size="14"
          class="mr-25"
        />
        {{ $t('despacho.filter.clear') }}
      </button>
    </div>

    <occurrence-filter-bar
      v-model="filterText"
      :applied="appliedTerm"
      :loading="loadingResultados"
      :count="occurrencesFiltradas.length"
      @submit="applyFilter"
    />

    <div
      v-if="loading"
      class="text-center py-5"
    >
      <b-spinner variant="primary" />
      <p class="mt-2 text-muted">
        {{ $t('despacho.loadingOccurrences') }}
      </p>
    </div>

    <template v-else>
      <!-- Minhas Ocorrências -->
      <div class="card mb-3">
        <div
          class="card-header d-flex align-items-center justify-content-between py-2"
        >
          <span class="font-weight-bold">
            <feather-icon
              icon="UserIcon"
              size="15"
              class="mr-50"
            />
            {{ $t('despacho.myOccurrences.title') }}
          </span>
          <span class="badge badge-primary">{{ myOccurrences.length }}</span>
        </div>
        <div class="card-body p-0">
          <div
            v-if="myOccurrences.length === 0"
            class="text-center py-4"
          >
            <p class="text-muted mb-0">
              {{ $t('despacho.myOccurrences.empty') }}
            </p>
          </div>
          <div
            v-else
            class="table-responsive"
          >
            <table class="table table-hover mb-0 occ-table">
              <thead class="thead-light">
                <tr>
                  <th>{{ $t('despacho.table.protocol') }}</th>
                  <th>{{ $t('despacho.table.requester') }}</th>
                  <th>{{ $t('despacho.table.phone') }}</th>
                  <th>{{ $t('despacho.table.address') }}</th>
                  <th>{{ $t('despacho.table.type') }}</th>
                  <th>{{ $t('despacho.table.reason') }}</th>
                  <th>{{ $t('despacho.table.discipline') }}</th>
                  <th>{{ $t('despacho.table.responsible') }}</th>
                  <th>{{ $t('common.status') }}</th>
                  <th>{{ $t('despacho.table.openedAt') }}</th>
                  <th>{{ $t('despacho.table.responseTime') }}</th>
                  <th
                    class="text-center"
                    style="width: 100px"
                  >
                    {{ $t('common.actions') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="occ in myOccurrences"
                  :key="occ.id"
                >
                  <td>
                    <span
                      class="badge text-monospace"
                      :style="protocolBadgeStyle(occ)"
                    >{{ occ.protocol }}</span>
                    <div
                      v-if="isFromChildNode(occ)"
                      class="occ-node-hint"
                      :title="occ.node && occ.node.name"
                    >
                      <feather-icon icon="GitBranchIcon" size="10" />
                      {{ occ.node && occ.node.name }}
                    </div>
                  </td>
                  <td>{{ occ.requester_name }}</td>
                  <td>{{ $occurrencePhone(occ) || "-" }}</td>
                  <td>
                    <span v-if="occ.address">
                      {{ occ.address }}{{ occ.number ? ", " + occ.number : "" }}
                      <span
                        v-if="occ.neighborhood"
                        class="text-muted"
                      >&mdash; {{ occ.neighborhood }}</span>
                    </span>
                    <span
                      v-else
                      class="text-muted"
                    >-</span>
                  </td>
                  <td>
                    {{ occ.dispatch_type ? occ.dispatch_type.name : "-" }}
                  </td>
                  <td>
                    {{ occ.dispatch_reason ? occ.dispatch_reason.name : "-" }}
                  </td>
                  <td>{{ occ.discipline ? occ.discipline.name : "-" }}</td>
                  <td>
                    {{
                      occ.active_responsibility &&
                        occ.active_responsibility.user
                        ? occ.active_responsibility.user.name
                        : "—"
                    }}
                  </td>
                  <td>
                    <div class="occ-status-cell">
                      <span
                        class="badge"
                        :style="dispatchStatusStyle(occ)"
                      >
                        {{ occ.dispatches_count > 0 ? $t('despacho.status.dispatched') : $t('despacho.status.pending') }}
                      </span>
                      <template v-if="occ.dispatches_count > 0">
                        <small
                          v-for="team in occ.dispatched_teams"
                          :key="team.dispatch_id"
                          class="occ-team-position"
                          :title="team.position_name"
                        >
                          {{ team.team_name }}<template v-if="team.position_code"> · {{ team.position_code }}</template>
                        </small>
                      </template>
                      <span
                        v-if="pendingByOccurrence[occ.id]"
                        class="occ-pending"
                        tabindex="0"
                      >
                        <span class="occ-pending-badge">
                          <feather-icon
                            icon="AlertTriangleIcon"
                            size="10"
                            class="mr-25"
                          />
                          {{ pendingByOccurrence[occ.id].total }}
                          {{ pendingByOccurrence[occ.id].total > 1 ? $t('despacho.pending.requests') : $t('despacho.pending.request') }}
                        </span>

                        <span
                          class="pend-pop"
                          role="tooltip"
                        >
                          <span class="pend-pop__head">{{ $t('despacho.pending.awaitingDispatch') }}</span>
                          <span
                            v-for="(item, i) in pendingByOccurrence[occ.id].items"
                            :key="i"
                            class="pend-pop__row"
                          >
                            <span class="pend-pop__dot" />
                            <span class="pend-pop__type">
                              {{ item.quantity }}× {{ item.team_type }}
                            </span>
                            <span class="pend-pop__state">
                              {{ item.quantity > item.remaining
                                ? $t('despacho.pending.missing', { remaining: item.remaining })
                                : $t('despacho.pending.pendingLabel') }}
                            </span>
                          </span>
                          <span class="pend-pop__foot">
                            {{ pendingByOccurrence[occ.id].units_remaining }}
                            {{ pendingByOccurrence[occ.id].units_remaining > 1
                              ? $t('despacho.pending.unitsMissing') : $t('despacho.pending.unitMissing') }}
                          </span>
                        </span>
                      </span>
                    </div>
                    <!-- Tempo na etapa, logo abaixo do status; o que passou do perigo vai na coluna ao lado. -->
                    <div class="mt-25">
                      <span
                        :class="elapsedBadgeClass(occ.current_step_since || occ.created_at, occ.current_step, occ.discipline_id)"
                      >
                        {{ elapsedHHMMSS(occ.current_step_since || occ.created_at) }}
                      </span>
                    </div>
                  </td>
                  <td>{{ formatDate(occ.created_at) }}</td>
                  <td>
                    <template v-if="responseTimeOf(occ).text">
                      <span
                        class="badge response-badge"
                        :class="{ piscar: responseTimeOf(occ).running }"
                        :style="responseBadgeStyle(occ)"
                        :title="$t('despacho.table.responseTimeHint')"
                      >{{ responseTimeOf(occ).text }}</span>
                    </template>
                    <span
                      v-else
                      class="text-muted"
                    >—</span>
                  </td>
                  <td class="text-center">
                    <div class="occ-actions">
                      <button
                        v-if="$hasAbility('despacho.visualizar')"
                        class="btn btn-sm btn-outline-primary"
                        :title="$t('despacho.view')"
                        @click="
                          $router.push({
                            name: 'despacho-detail',
                            params: { id: occ.id },
                          })
                        "
                      >
                        <feather-icon
                          icon="EyeIcon"
                          size="13"
                        />
                      </button>
                      <button
                        v-if="$hasAbility('despacho.visualizar')"
                        class="btn btn-sm btn-outline-secondary"
                        :title="$t('occurrenceTable.actions.viewMap')"
                        @click="verNoMapa(occ)"
                      >
                        <feather-icon
                          icon="MapPinIcon"
                          size="13"
                        />
                      </button>
                      <button
                        v-if="podeRedirecionar(occ)"
                        class="btn btn-sm btn-outline-warning"
                        :title="$t('occurrenceTable.actions.redirectDispatch')"
                        @click="openRedirect(occ)"
                      >
                        <feather-icon
                          icon="ShuffleIcon"
                          size="13"
                        />
                      </button>
                      <button
                        v-if="occ.dispatches_count > 0 && podeCancelarDespacho"
                        class="btn btn-sm btn-outline-danger"
                        :title="$t('occurrenceTable.actions.cancelDispatch')"
                        @click="cancelDispatch(occ)"
                      >
                        <feather-icon
                          icon="XCircleIcon"
                          size="13"
                        />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Ocorrências da Central -->
      <div
        v-if="$hasAbility('despacho.ver_oc_central')"
        class="card"
      >
        <div
          class="card-header d-flex align-items-center justify-content-between py-2"
        >
          <span class="font-weight-bold">
            <feather-icon
              icon="GridIcon"
              size="15"
              class="mr-50"
            />
            {{ $t('despacho.centralOccurrences.title') }}
          </span>
          <span class="badge badge-secondary">{{
            nodeOccurrences.length
          }}</span>
        </div>
        <div class="card-body p-0">
          <div
            v-if="nodeOccurrences.length === 0"
            class="text-center py-4"
          >
            <feather-icon
              icon="InboxIcon"
              size="40"
              class="text-muted mb-2"
            />
            <p class="text-muted mb-0">
              {{ $t('despacho.centralOccurrences.empty') }}
            </p>
          </div>
          <div
            v-else
            class="table-responsive"
          >
            <table class="table table-hover mb-0 occ-table">
              <thead class="thead-light">
                <tr>
                  <th>{{ $t('despacho.table.protocol') }}</th>
                  <th>{{ $t('despacho.table.requester') }}</th>
                  <th>{{ $t('despacho.table.phone') }}</th>
                  <th>{{ $t('despacho.table.address') }}</th>
                  <th>{{ $t('despacho.table.type') }}</th>
                  <th>{{ $t('despacho.table.reason') }}</th>
                  <th>{{ $t('despacho.table.discipline') }}</th>
                  <th>{{ $t('despacho.table.responsible') }}</th>
                  <th>{{ $t('common.status') }}</th>
                  <th>{{ $t('despacho.table.openedAt') }}</th>
                  <th>{{ $t('despacho.table.responseTime') }}</th>
                  <th
                    class="text-center"
                    style="width: 100px"
                  >
                    {{ $t('common.actions') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="occ in nodeOccurrences"
                  :key="occ.id"
                  :class="{ 'occ-row--mine': isMine(occ) }"
                >
                  <td>
                    <span
                      class="badge text-monospace"
                      :style="protocolBadgeStyle(occ)"
                    >{{ occ.protocol }}</span>
                    <div
                      v-if="isFromChildNode(occ)"
                      class="occ-node-hint"
                      :title="occ.node && occ.node.name"
                    >
                      <feather-icon icon="GitBranchIcon" size="10" />
                      {{ occ.node && occ.node.name }}
                    </div>
                  </td>
                  <td>{{ occ.requester_name }}</td>
                  <td>{{ $occurrencePhone(occ) || "-" }}</td>
                  <td>
                    <span v-if="occ.address">
                      {{ occ.address }}{{ occ.number ? ", " + occ.number : "" }}
                      <span
                        v-if="occ.neighborhood"
                        class="text-muted"
                      >&mdash; {{ occ.neighborhood }}</span>
                    </span>
                    <span
                      v-else
                      class="text-muted"
                    >-</span>
                  </td>
                  <td>
                    {{ occ.dispatch_type ? occ.dispatch_type.name : "-" }}
                  </td>
                  <td>
                    {{ occ.dispatch_reason ? occ.dispatch_reason.name : "-" }}
                  </td>
                  <td>{{ occ.discipline ? occ.discipline.name : "-" }}</td>
                  <td>
                    <span
                      v-if="isMine(occ)"
                      class="occ-mine-label"
                    >{{ $t('despacho.you') }}</span>
                    <template v-else>
                      {{
                        occ.active_responsibility &&
                          occ.active_responsibility.user
                          ? occ.active_responsibility.user.name
                          : "—"
                      }}
                    </template>
                  </td>
                  <td>
                    <div class="occ-status-cell">
                      <span
                        class="badge"
                        :style="dispatchStatusStyle(occ)"
                      >
                        {{ occ.dispatches_count > 0 ? $t('despacho.status.dispatched') : $t('despacho.status.pending') }}
                      </span>
                      <template v-if="occ.dispatches_count > 0">
                        <small
                          v-for="team in occ.dispatched_teams"
                          :key="team.dispatch_id"
                          class="occ-team-position"
                          :title="team.position_name"
                        >
                          {{ team.team_name }}<template v-if="team.position_code"> · {{ team.position_code }}</template>
                        </small>
                      </template>
                      <span
                        v-if="pendingByOccurrence[occ.id]"
                        class="occ-pending"
                        tabindex="0"
                      >
                        <span class="occ-pending-badge">
                          <feather-icon
                            icon="AlertTriangleIcon"
                            size="10"
                            class="mr-25"
                          />
                          {{ pendingByOccurrence[occ.id].total }}
                          {{ pendingByOccurrence[occ.id].total > 1 ? $t('despacho.pending.requests') : $t('despacho.pending.request') }}
                        </span>

                        <span
                          class="pend-pop"
                          role="tooltip"
                        >
                          <span class="pend-pop__head">{{ $t('despacho.pending.awaitingDispatch') }}</span>
                          <span
                            v-for="(item, i) in pendingByOccurrence[occ.id].items"
                            :key="i"
                            class="pend-pop__row"
                          >
                            <span class="pend-pop__dot" />
                            <span class="pend-pop__type">
                              {{ item.quantity }}× {{ item.team_type }}
                            </span>
                            <span class="pend-pop__state">
                              {{ item.quantity > item.remaining
                                ? $t('despacho.pending.missing', { remaining: item.remaining })
                                : $t('despacho.pending.pendingLabel') }}
                            </span>
                          </span>
                          <span class="pend-pop__foot">
                            {{ pendingByOccurrence[occ.id].units_remaining }}
                            {{ pendingByOccurrence[occ.id].units_remaining > 1
                              ? $t('despacho.pending.unitsMissing') : $t('despacho.pending.unitMissing') }}
                          </span>
                        </span>
                      </span>
                    </div>
                    <!-- Tempo na etapa, logo abaixo do status; o que passou do perigo vai na coluna ao lado. -->
                    <div class="mt-25">
                      <span
                        :class="elapsedBadgeClass(occ.current_step_since || occ.created_at, occ.current_step, occ.discipline_id)"
                      >
                        {{ elapsedHHMMSS(occ.current_step_since || occ.created_at) }}
                      </span>
                    </div>
                  </td>
                  <td>{{ formatDate(occ.created_at) }}</td>
                  <td>
                    <template v-if="responseTimeOf(occ).text">
                      <span
                        class="badge response-badge"
                        :class="{ piscar: responseTimeOf(occ).running }"
                        :style="responseBadgeStyle(occ)"
                        :title="$t('despacho.table.responseTimeHint')"
                      >{{ responseTimeOf(occ).text }}</span>
                    </template>
                    <span
                      v-else
                      class="text-muted"
                    >—</span>
                  </td>
                  <td class="text-center">
                    <div class="occ-actions">
                      <button
                        v-if="$hasAbility('despacho.visualizar')"
                        class="btn btn-sm btn-outline-primary"
                        :title="$t('despacho.view')"
                        @click="
                          $router.push({
                            name: 'despacho-detail',
                            params: { id: occ.id },
                          })
                        "
                      >
                        <feather-icon
                          icon="EyeIcon"
                          size="13"
                        />
                      </button>
                      <button
                        v-if="$hasAbility('despacho.visualizar')"
                        class="btn btn-sm btn-outline-secondary"
                        :title="$t('occurrenceTable.actions.viewMap')"
                        @click="verNoMapa(occ)"
                      >
                        <feather-icon
                          icon="MapPinIcon"
                          size="13"
                        />
                      </button>
                      <button
                        v-if="podeRedirecionar(occ)"
                        class="btn btn-sm btn-outline-warning"
                        :title="$t('occurrenceTable.actions.redirectDispatch')"
                        @click="openRedirect(occ)"
                      >
                        <feather-icon
                          icon="ShuffleIcon"
                          size="13"
                        />
                      </button>
                      <button
                        v-if="occ.dispatches_count > 0 && podeCancelarDespacho"
                        class="btn btn-sm btn-outline-danger"
                        :title="$t('occurrenceTable.actions.cancelDispatch')"
                        @click="cancelDispatch(occ)"
                      >
                        <feather-icon
                          icon="XCircleIcon"
                          size="13"
                        />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </template>

    <OccurrenceMapModal
      :visible.sync="showMapModal"
      :occurrence-id="mapOccurrenceId"
    />

    <!-- Sem dispatch-id: o modal lista os despachos ativos e pergunta qual. -->
    <RedirectDispatchModal
      :visible.sync="showRedirectModal"
      :occurrence-id="redirectOccurrence ? redirectOccurrence.id : null"
      @redirected="reloadQueue"
    />

    <b-modal
      v-model="showCancelDispatchModal"
      :title="$t('despacho.detail.cancelDispatchModalTitle')"
      :ok-title="$t('despacho.detail.confirmCancelButton')"
      :cancel-title="$t('common.back')"
      ok-variant="danger"
      :ok-disabled="cancellingDispatch"
      @ok="confirmCancelDispatch"
    >
      <div class="form-group">
        <label>{{ $t('despacho.detail.cancelReasonLabel') }}</label>
        <textarea
          v-model="cancelDispatchNotes"
          class="form-control"
          rows="3"
          :placeholder="$t('despacho.detail.cancelReasonPlaceholder')"
          maxlength="500"
        />
      </div>
    </b-modal>
  </div>
</template>

<script>
import ApiErrorMixin from '@/mixins/ApiErrorMixin'
import { BSpinner } from 'bootstrap-vue'
import ElapsedTimeMixin from '@/mixins/ElapsedTimeMixin'
import RealtimeResyncMixin from '@/mixins/RealtimeResyncMixin'
import OccurrenceFeedMixin from '@/mixins/OccurrenceFeedMixin'
import DispatchEventsMixin from '@/mixins/DispatchEventsMixin'
import OccurrenceFilterMixin from '@/mixins/OccurrenceFilterMixin'
import OccurrenceFilterBar from '@/components/OccurrenceFilterBar.vue'
import OccurrenceMapModal from '@/components/OccurrenceMapModal.vue'
import RedirectDispatchModal from '@/components/RedirectDispatchModal.vue'
import { getContrastTextColor } from '@/utils/color'

export default {
  name: 'DespachoIndex',
  components: {
    BSpinner, OccurrenceFilterBar, OccurrenceMapModal, RedirectDispatchModal,
  },
  mixins: [ElapsedTimeMixin, ApiErrorMixin, RealtimeResyncMixin, OccurrenceFeedMixin, DispatchEventsMixin, OccurrenceFilterMixin],
  data() {
    return {
      occurrences: [],
      pendingByOccurrence: {},
      loading: false,
      loadingKpis: false,
      refreshTimer: null,
      kpis: {},
      filtroKpi: null,
      resultados: null,
      loadingResultados: false,
      resultadosRequestId: 0,
      showMapModal: false,
      mapOccurrenceId: null,
      showCancelDispatchModal: false,
      cancelDispatchOccurrence: null,
      showRedirectModal: false,
      redirectOccurrence: null,
      cancelDispatchNotes: '',
      cancellingDispatch: false,
    }
  },
  computed: {
    podeCancelarDespacho() {
      return this.$hasAbility('despacho.cancelar')
    },
    kpiCards() {
      const k = this.kpis
      return [
        {
          key: 'total_open',
          filterable: true,
          label: this.$t('despacho.kpi.open'),
          value: k.total_open ?? '—',
          icon: 'AlertCircleIcon',
          variant: 'kpi-card--primary',
        },
        {
          key: 'my_open',
          filterable: true,
          label: this.$t('despacho.kpi.mine'),
          value: k.my_open ?? '—',
          icon: 'UserIcon',
          variant: 'kpi-card--info',
        },
        {
          key: 'without_responsible',
          filterable: true,
          label: this.$t('despacho.kpi.pending'),
          value: this.occurrences.filter(o => !o.dispatches_count).length,
          icon: 'AlertTriangleIcon',
          variant: this.occurrences.some(o => !o.dispatches_count)
            ? 'kpi-card--danger'
            : 'kpi-card--success',
        },
        {
          key: 'last_1h',
          filterable: false,
          label: this.$t('despacho.kpi.last1h'),
          value: k.last_1h ?? '—',
          icon: 'ClockIcon',
          variant: 'kpi-card--secondary',
        },
        {
          key: 'last_24h',
          filterable: false,
          label: this.$t('despacho.kpi.last24h'),
          value: k.last_24h ?? '—',
          icon: 'CalendarIcon',
          variant: 'kpi-card--secondary',
        },
        {
          key: 'avg_seconds_to_assume',
          filterable: false,
          label: this.$t('despacho.kpi.avgTimeToAssume'),
          value:
            k.avg_seconds_to_assume != null
              ? this.formatSeconds(k.avg_seconds_to_assume)
              : '—',
          icon: 'ZapIcon',
          variant: 'kpi-card--secondary',
        },
      ]
    },
    rotuloFiltroKpi() {
      const card = this.kpiCards.find(c => c.key === this.filtroKpi)
      return card ? card.label : ''
    },
    listaBase() {
      return this.appliedTerm && this.resultados ? this.resultados : this.occurrences
    },
    occurrencesFiltradas() {
      const filtro = this.filtroKpi
      if (!filtro || filtro === 'total_open') return this.listaBase

      if (filtro === 'my_open') {
        const uid = this.$store.state.user.id
        return this.listaBase.filter(
          o => o.active_responsibility && o.active_responsibility.user_id === uid,
        )
      }

      if (filtro === 'without_responsible') {
        return this.listaBase.filter(o => !o.dispatches_count)
      }

      return this.listaBase
    },
    myOccurrences() {
      const uid = this.$store.state.user.id
      return this.occurrencesFiltradas.filter(
        o => o.active_responsibility && o.active_responsibility.user_id === uid,
      )
    },
    nodeOccurrences() {
      return this.occurrencesFiltradas
    },
  },
  mounted() {
    this.loadOccurrences()
    this.loadKpis()
    this.refreshTimer = setInterval(() => {
      this.loadOccurrences()
      this.loadKpis()
    }, 180000)

    this.watchOccurrenceFeed()
    this.watchRealtimeResync(() => this.reloadQueue())
    this.watchDispatchEvents()
  },
  beforeDestroy() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
    }
  },
  methods: {
    isMine(occ) {
      return !!(occ.active_responsibility && occ.active_responsibility.user_id === this.$store.state.user.id)
    },

    verNoMapa(occ) {
      this.mapOccurrenceId = occ.id
      this.showMapModal = true
    },

    // Mesma regra do detalhe, por despacho: a posição atual da equipe
    // permite redirecionamento (flag allows_redirect) e o nó pode
    // redirecioná-lo. Basta um; o modal pergunta qual.
    podeRedirecionar(occ) {
      return this.podeCancelarDespacho
        && (occ.redirect_candidates || []).some(c => c.allows_redirect && c.redirectable_by_node)
    },

    openRedirect(occ) {
      this.redirectOccurrence = occ
      this.showRedirectModal = true
    },

    cancelDispatch(occ) {
      this.cancelDispatchOccurrence = occ
      this.cancelDispatchNotes = ''
      this.showCancelDispatchModal = true
    },

    async confirmCancelDispatch(bvModalEvent) {
      bvModalEvent.preventDefault()
      const occ = this.cancelDispatchOccurrence
      if (!occ || !occ.active_dispatch_id) return

      this.cancellingDispatch = true
      try {
        await this.$http.post(`${this.$store.state.api}occurrence_dispatches/${occ.active_dispatch_id}/cancel`, {
          notes: this.cancelDispatchNotes || null,
        })
        this.showCancelDispatchModal = false
        this.reloadQueue()
      } catch (e) {
        this.$message(this.$t('despacho.messages.error'), this.apiErrorText(e, this.$t('despacho.messages.cancelDispatchError')), 'error')
      } finally {
        this.cancellingDispatch = false
      }
    },

    toggleKpi(card) {
      if (!card.filterable) return
      this.filtroKpi = this.filtroKpi === card.key ? null : card.key
    },

    reloadQueue() {
      this.loadOccurrences()
      this.loadKpis()
      this.loadResultados()
    },

    applyOccurrenceFeed() {
      this.scheduleRealtimeReload(() => {
        this.clearOccurrenceFeed()
        this.reloadQueue()
      })
    },

    onDispatchEvent(kind, payload) {
      // Posicao de equipe nao muda quem esta na fila, so o rotulo exibido
      // embaixo do badge "Despachado" — atualiza so essa ocorrencia, sem
      // recarregar a fila inteira (seria recarga constante numa central com
      // dezenas de equipes reportando J's).
      if (kind === 'position') {
        this.applyTeamPositionUpdate(payload)
        return
      }

      this.scheduleRealtimeReload(() => this.reloadQueue())
    },

    applyTeamPositionUpdate(payload) {
      if (!payload || !payload.occurrence_id) return
      const occ = this.occurrences.find(o => o.id === payload.occurrence_id)
      if (!occ) return

      this.$set(occ, 'current_team_position_code', payload.code || null)
      this.$set(occ, 'current_team_position_name', payload.name || null)
      const team = (occ.dispatched_teams || [])
        .find(t => Number(t.dispatch_id) === Number(payload.occurrence_dispatch_id))
      if (team) {
        this.$set(team, 'position_code', payload.code || null)
        this.$set(team, 'position_name', payload.name || null)
      }
      // O botão Redirecionar acompanha a flag da posição nova sem recarregar.
      const candidate = (occ.redirect_candidates || [])
        .find(c => Number(c.dispatch_id) === Number(payload.occurrence_dispatch_id))
      if (candidate) this.$set(candidate, 'allows_redirect', !!payload.allows_redirect)
    },
    listParams() {
      return {
        with: [
          'activeResponsibility.user',
          'classification',
          'dispatchReason',
          'discipline',
        ],
        current_step: 'despacha',
        context: 'despacho',
      }
    },

    async loadOccurrences() {
      this.loading = this.occurrences.length === 0
      try {
        const params = this.listParams()
        const res = await this.$http.get(
          `${this.$store.state.api}occurrences`,
          { params },
        )
        this.occurrences = res.data.data || res.data || []
        await this.loadPendingTeamRequests(this.occurrences.map(o => o.id))
      } catch (e) {
        this.$message(this.$t('despacho.messages.error'), this.apiErrorText(e, this.$t('despacho.messages.loadError')), 'error')
      } finally {
        this.loading = false
      }
    },

    onFilterApplied() {
      this.loadResultados()
    },

    async loadResultados() {
      const termo = this.appliedTerm
      this.resultadosRequestId += 1
      const requestId = this.resultadosRequestId

      if (!termo) {
        this.resultados = null
        this.loadingResultados = false
        return
      }

      this.loadingResultados = true
      try {
        const res = await this.$http.get(`${this.$store.state.api}occurrences`, {
          params: { ...this.listParams(), search: termo },
        })
        if (requestId !== this.resultadosRequestId) return
        this.resultados = res.data.data || []
      } catch (e) {
        if (requestId !== this.resultadosRequestId) return
        this.$message(this.$t('despacho.messages.error'), this.apiErrorText(e, this.$t('despacho.messages.filterError')), 'error')
      } finally {
        if (requestId === this.resultadosRequestId) this.loadingResultados = false
      }
    },

    async loadPendingTeamRequests(occurrenceIds) {
      if (!occurrenceIds.length) {
        this.pendingByOccurrence = {}
        return
      }
      try {
        const res = await this.$http.get(
          `${this.$store.state.api}occurrence_team_requests/pending-count`,
          { params: { occurrence_ids: occurrenceIds } },
        )
        this.pendingByOccurrence = res.data.data || {}
      } catch (e) {
        this.pendingByOccurrence = {}
      }
    },

    async loadKpis() {
      this.loadingKpis = Object.keys(this.kpis).length === 0
      try {
        const res = await this.$http.get(
          `${this.$store.state.api}occurrences/step-kpis`,
          { params: { step: 'despacha' } },
        )
        this.kpis = res.data.data || {}
      } catch (_) {
        // KPI e informativo: falhar nele nao pode derrubar a fila
      } finally {
        this.loadingKpis = false
      }
    },

    formatSeconds(seconds) {
      if (seconds == null) return '—'
      const h = Math.floor(seconds / 3600)
      const m = Math.floor((seconds % 3600) / 60)
      const s = seconds % 60
      if (h > 0) return `${h}h ${m}m`
      if (m > 0) return `${m}m ${s}s`
      return `${s}s`
    },

    formatDate(value) {
      if (!value) return '-'
      return new Date(value).toLocaleString('pt-BR')
    },

    dispatchStatusStyle(occ) {
      return occ.dispatches_count > 0
        ? { backgroundColor: '#2563eb', color: '#fff' }
        : { backgroundColor: '#f59e0b', color: '#fff' }
    },

    protocolBadgeStyle(occ) {
      const bg = (occ.classification && occ.classification.hex_color) || '#7367f0'
      return { backgroundColor: bg, color: getContrastTextColor(bg) }
    },

    isFromChildNode(occ) {
      const selectedNodeId = this.$store.state.selected_node?.id
      return !!occ.node_id && !!selectedNodeId && occ.node_id !== selectedNodeId
    },
  },
}
</script>

<style scoped>
/* Tempo-resposta: selo na cor da classificação; pisca enquanto nenhuma equipe chegou ao local. */
.response-badge {
  font-variant-numeric: tabular-nums;
  font-weight: 700;
}

@keyframes piscar {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}

.piscar {
  animation: piscar 1.5s ease-in-out infinite;
}

@media (prefers-reduced-motion: reduce) {
  .piscar { animation: none; }
}

.occ-node-hint {
  font-size: 10px;
  color: #4b4b4b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 110px;
}
.occ-table {
  font-size: 0.85rem;
}
.occ-table th,
.occ-table td {
  padding: 0.5rem 0.75rem;
  vertical-align: middle;
}
.occ-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}
.occ-row--mine td {
  background-color: rgba(115, 103, 240, 0.06);
}
.occ-row--mine td:first-child {
  box-shadow: inset 3px 0 0 #7367f0;
}
.occ-mine-label {
  font-weight: 700;
  color: #7367f0;
}
.dark-layout .occ-row--mine td {
  background-color: rgba(115, 103, 240, 0.14);
}

.kpi-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid #ebe9f1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  height: 100%;
}

.kpi-card__icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.kpi-card__value {
  font-size: 1.4rem;
  font-weight: 700;
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
}

.kpi-card__label {
  font-size: 11px;
  color: #b9b9c3;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-top: 2px;
}

.kpi-card--primary .kpi-card__icon {
  background: #ede9fd;
  color: #7367f0;
}
.kpi-card--primary .kpi-card__value {
  color: #7367f0;
}

.kpi-card--info .kpi-card__icon {
  background: #d4f4e6;
  color: #00cfe8;
}
.kpi-card--info .kpi-card__value {
  color: #00cfe8;
}

.kpi-card--danger {
  border-color: #ea5455;
  border-width: 2px;
  background: #fff5f5;
  animation: kpi-pulse 2s ease-in-out infinite;
}
.kpi-card--danger .kpi-card__icon {
  background: #ea5455;
  color: #fff;
  width: 48px;
  height: 48px;
}
.kpi-card--danger .kpi-card__value {
  color: #ea5455;
  font-size: 1.9rem;
}
.kpi-card--danger .kpi-card__label {
  color: #ea5455;
  font-weight: 700;
}

@keyframes kpi-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(234, 84, 85, 0.25);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(234, 84, 85, 0);
  }
}

.kpi-card--success .kpi-card__icon {
  background: #e8faf0;
  color: #28c76f;
}
.kpi-card--success .kpi-card__value {
  color: #28c76f;
}

.kpi-card--secondary .kpi-card__icon {
  background: #f3f2f7;
  color: #6e6b7b;
}
.kpi-card--secondary .kpi-card__value {
  color: #5e5873;
}

/* Dark theme */
.dark-layout .kpi-card {
  background: #283046;
  border-color: #3b4253;
}
.dark-layout .kpi-card__label {
  color: #676d7d;
}

.kpi-card--clickable {
  cursor: pointer;
  user-select: none;
  transition: transform 0.12s ease, box-shadow 0.12s ease,
    border-color 0.12s ease;
}
.kpi-card--clickable:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.1);
}
.kpi-card--clickable:focus-visible {
  outline: 2px solid #7367f0;
  outline-offset: 2px;
}
.kpi-card--active {
  border-color: #7367f0;
  box-shadow: 0 0 0 2px rgba(115, 103, 240, 0.35);
}
.kpi-card--danger.kpi-card--active {
  border-color: #ea5455;
  box-shadow: 0 0 0 2px rgba(234, 84, 85, 0.35);
  animation: none;
}
.dark-layout .kpi-card--clickable:hover {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.45);
}

.kpi-filter {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #f3f2fa;
  border: 1px solid #d8d4f2;
  color: #5e5873;
  font-size: 13px;
}
.dark-layout .kpi-filter {
  background: #2f3349;
  border-color: #3b4253;
  color: #d0d2d6;
}

.occ-status-cell {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}
.occ-team-position {
  font-size: 11px;
  font-weight: 700;
  color: #6e6b7b;
}
.dark-layout .occ-team-position {
  color: #b4b7bd;
}
.occ-pending-badge {
  display: inline-flex;
  align-items: center;
  padding: 1px 7px;
  border-radius: 10px;
  background: #fff3e6;
  color: #ff9f43;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.7;
  white-space: nowrap;
}
.dark-layout .occ-pending-badge {
  background: #3d2f1c;
}

.occ-pending {
  position: relative;
  display: inline-flex;
  outline: none;
}
.occ-pending:focus-visible .occ-pending-badge {
  outline: 2px solid #ff9f43;
  outline-offset: 2px;
}

.pend-pop {
  position: absolute;
  right: calc(100% + 10px);
  top: 50%;
  transform: translateY(-50%);
  z-index: 30;
  display: none;
  flex-direction: column;
  min-width: 190px;
  padding: 9px 11px;
  border-radius: 7px;
  background: #2f3349;
  color: #fff;
  box-shadow: 0 6px 20px -6px rgba(0, 0, 0, 0.45);
  text-align: left;
  white-space: nowrap;
}
.occ-pending:hover .pend-pop,
.occ-pending:focus-visible .pend-pop {
  display: flex;
}
.pend-pop::after {
  content: "";
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  border: 6px solid transparent;
  border-left-color: #2f3349;
}

.pend-pop__head {
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  opacity: 0.6;
  margin-bottom: 5px;
}
.pend-pop__row {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 2px 0;
  font-size: 12px;
}
.pend-pop__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #ff9f43;
  flex: 0 0 6px;
}
.pend-pop__type { font-weight: 600; }
.pend-pop__state {
  margin-left: auto;
  padding-left: 14px;
  color: #ffb976;
  font-size: 11px;
  font-weight: 600;
}
.pend-pop__foot {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  font-size: 10.5px;
  opacity: 0.7;
}
</style>
